import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent
} from '@mui/material';

export interface ConfirmReferralDialogRecipient {
  facility_id: string;
  facility_name?: string | null;
}

interface ConfirmReferralDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (facilityId: string) => void;
  isLoading: boolean;
  acceptedRecipients: ConfirmReferralDialogRecipient[];
  patientName: string;
}

export const ConfirmReferralDialog: React.FC<ConfirmReferralDialogProps> = ({
  open,
  onClose,
  onConfirm,
  isLoading,
  acceptedRecipients,
  patientName
}) => {
  const [selectedFacilityId, setSelectedFacilityId] = useState('');

  useEffect(() => {
    if (!open) {
      setSelectedFacilityId('');
    }
  }, [open]);

  const handleChange = (event: SelectChangeEvent) => {
    setSelectedFacilityId(event.target.value);
  };

  const handleConfirm = () => {
    if (selectedFacilityId) {
      onConfirm(selectedFacilityId);
    }
  };

  const handleClose = () => {
    setSelectedFacilityId('');
    onClose();
  };

  const patientLabel = patientName ? ` for ${patientName}` : '';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Confirm Referral</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Select the facility to confirm{patientLabel}. All other recipients
          will be closed.
        </DialogContentText>
        <FormControl fullWidth size="small">
          <InputLabel id="confirm-facility-label">Facility</InputLabel>
          <Select
            labelId="confirm-facility-label"
            value={selectedFacilityId}
            label="Facility"
            onChange={handleChange}
          >
            {acceptedRecipients.map((r) => (
              <MenuItem key={r.facility_id} value={r.facility_id}>
                {r.facility_name ?? r.facility_id}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          loading={isLoading}
          disabled={!selectedFacilityId}
          variant="contained"
          color="success"
        >
          Confirm Referral
        </Button>
      </DialogActions>
    </Dialog>
  );
};
